
import { defineComponent, PropType } from 'vue';
import { ActionTypes } from '@/store/actionTypes';
import ConfirmationOverlay from '@/components/ConfirmationOverlay.vue';
import { TrailerAssessmentOutcome } from '@/types/TrailerAssessmentOutcome';
import { isNullOrUndefined } from '@/shared/helpers';
import FbaApiError from '@/types/FbaApiError';
import { MutationTypes } from '@/store/mutationTypes';
import { AssociationType } from '@/types/AssociationType';
import TrailerAssessment from '@/types/TrailerAssessment';
import globalConstants from '@/shared/globalConstants';

export default defineComponent({
    components: {
        ConfirmationOverlay,
    },
    data(): {
        showLoading: 'accept' | 'reject' | null;
        signatureRecordedTimestamp: string | null;
        submitting: boolean;
    } {
        return {
            showLoading: null,
            signatureRecordedTimestamp: null,
            submitting: false,
        };
    },
    props: {
        company: {
            type: String,
            required: true,
        },
        trailerNumber: {
            type: String,
            required: true,
        },
        association: {
            type: String as PropType<AssociationType>,
            required: true,
        },
    },
    computed: {
        assistanceCompany(): string {
            return this.company === 'knight'
                ? globalConstants.trailer.assistance.knight
                : globalConstants.trailer.assistance.swift;
        },
    },
    methods: {
        accept(): void {
            this.$emit('accepted', { didAccept: true });
        },
        async reject(): Promise<void> {
            this.submitting = true;
            this.showLoading = 'reject';

            this.$store.dispatch(
                ActionTypes.UPDATE_TRAILER_ASSESSMENT_OUTCOME,
                TrailerAssessmentOutcome.DECLINED_DUE_TO_DOT_EXPIRATION
            );

            let response;
            try {
                response = await this.postAssessment();
            } finally {
                this.submitting = false;
                this.showLoading = null;
            }

            if (response === false || isNullOrUndefined(response)) {
                this.$store.commit(
                    MutationTypes.SET_ERROR,
                    new FbaApiError('Trailer attach/detach association failed')
                );
                return;
            }

            this.$router.push({
                name: 'TrailerNotFit',
                query: {
                    trailerNumber: this.trailerNumber,
                    wasNonDamageIssue: 'true',
                },
            });
        },
        async postAssessment(): Promise<boolean> {
            this.$store.commit(
                MutationTypes.UPDATE_TRAILER_ASSESSMENT,
                new TrailerAssessment()
            );
            const hasPhotos = this.$store.getters.trailerPhotosCount > 0;
            const assessmentResponse = await this.$store.dispatch(
                ActionTypes.POST_TRAILER_ASSESSMENT,
                {
                    trailerNumber: this.trailerNumber,
                    trailerCompany: this.company,
                    eventName: this.association,
                    damageSignatureRecordedTimestamp:
                        this.signatureRecordedTimestamp,
                    hasPhotos,
                    source: globalConstants.trailer.trailerAssessment.source,
                    entryPoint:
                        globalConstants.trailer.trailerAssessment.entryPoints
                            .attach,
                }
            );

            if (assessmentResponse instanceof FbaApiError) {
                this.$store.commit(
                    MutationTypes.SET_ERROR,
                    assessmentResponse as FbaApiError
                );
                return false;
            }

            if (
                assessmentResponse?.didSucceed &&
                assessmentResponse?.assessmentId &&
                hasPhotos
            ) {
                await this.$store.dispatch(
                    ActionTypes.SUBMIT_TRAILER_PHOTOS,
                    assessmentResponse.assessmentId
                );
            }

            return assessmentResponse?.didSucceed;
        },
    },
});
